import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Backdrop,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmblaCarousel from "../carousel/EmblaCarousel";
import { STORAGE_BUCKET, IMG_PLACEHOLDER } from "../../constants";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloudinaryUploadWidget from "../CloudinaryUploadWidget";
import ConfigContext from "../../context/ConfigContext";

export default function Images(props) {
  const siteConfig = useContext(ConfigContext);

  const [pics, setPics] = useState(props.pics || []);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);

  let style = props.style || { width: "100%", aspectRatio: "1", objectFit: "cover" };

  useEffect(() => {
    if (props.onChange) {
      if (JSON.stringify(pics) !== JSON.stringify(props.pics)) {
        props.onChange(pics);
      }
    }
  }, [pics]);

  const close = () => {
    setSelectedIdx(0);
    setOpen(false);
  };

  const remove = (idx) => {
    let arr = [...pics];
    arr.splice(idx, 1);
    setPics(arr);
    if (arr.length === 0) {
      close();
    } else {
      setSelectedIdx((prev) => Math.min(prev, arr.length - 1));
    }
  };

  const inputComponent =
    props.max > pics.length ? (
      <CloudinaryUploadWidget
        folder={`s/${siteConfig.id}/item`}
        onComplete={(newPics) => {
          const arr = pics.concat(newPics);
          setPics(arr);
        }}
        max={props.max}
      >
        <Button fullWidth variant="contained" color="secondary" tabIndex={-1}>
          UPLOAD
        </Button>
      </CloudinaryUploadWidget>
    ) : (
      ""
    );

  const backdrop = (
    <Backdrop open={working} style={{ zIndex: "1000", color: "#fff" }}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );

  let placeholderImg = `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`;

  const uploadPlaceholder = (
    <Link
      aria-label="upload images"
      component="label"
      style={{ textAlign: "center", cursor: "pointer" }}
      underline="hover"
    >
      <Box>
        {backdrop}
        <img alt="" src={placeholderImg} style={style} />
        {inputComponent}
      </Box>
    </Link>
  );

  let header = (
    <Box sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
      <IconButton color="inherit" onClick={close} aria-label="close image carousel">
        <ArrowBackIosNewIcon />
      </IconButton>
      <Box
        sx={{
          flexGrow: "1",
          textAlign: "center",
          display: "flex",
          gap: "10px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="overline">{`${pics.length} photos`}</Typography>
        {props.edit && inputComponent}
      </Box>
      <IconButton color="inherit" onClick={close} aria-label="close image carousel">
        <CloseIcon />
      </IconButton>
    </Box>
  );

  return (
    <Badge badgeContent={pics.length > 1 ? pics.length : 0} color="info">
      <Box
        id="images"
        style={{
          cursor: "pointer",
          backgroundColor: "#ECECEC",
          borderRadius: "8px",
          overflow: "hidden",
          width: "100%",
        }}
        align="center"
      >
        {props.edit && pics.length === 0 ? (
          uploadPlaceholder
        ) : (
          <Box>
            <Box onClick={() => setOpen(true)}>
              <EmblaCarousel
                autoPlay={false}
                infiniteLoop={false}
                showArrows={false}
                interval={5000}
                transitionTime={1000}
                sx={{ aspectRatio: style.aspectRatio || "1" }}
              >
                {(pics && pics.length > 0 ? pics : [placeholderImg]).map((pic, idx) => {
                  return (
                    <Box key={idx} sx={{ width: "100%", height: "100%" }}>
                      <img alt="" src={pic} style={style} />
                    </Box>
                  );
                })}
              </EmblaCarousel>
            </Box>
            {props.edit && <Box>{inputComponent}</Box>}
          </Box>
        )}
        <Dialog fullScreen open={open} onClose={close}>
          <DialogContent
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {header}
            {backdrop}
            <Box mt={2} align="center" sx={{ flex: 1, minHeight: 0 }}>
              <EmblaCarousel
                autoPlay={false}
                infiniteLoop={false}
                transitionTime={1000}
                initialIndex={selectedIdx}
                onSelect={setSelectedIdx}
                sx={{ height: "100%" }}
              >
                {pics.map((pic, idx) => {
                  return (
                    <Box key={`d${idx}`} style={{ color: "#CCC", height: "100%" }}>
                      <Grid container spacing={1} alignItems="center" sx={{ height: "100%" }}>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            alt=""
                            src={pic}
                            style={{ maxHeight: "100%", objectFit: "contain" }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </EmblaCarousel>
            </Box>
            {props.edit && pics.length > 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  pt: 2,
                  pb: "max(16px, env(safe-area-inset-bottom))",
                }}
              >
                <Button
                  type="button"
                  color="inherit"
                  onClick={() => remove(selectedIdx)}
                  sx={{ textDecoration: "underline" }}
                >
                  REMOVE
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Badge>
  );
}
